import request from '../request';
import { API_ENDPOINT } from '../../config';
import type { MessageSearchHit } from '../../routes/Messaging/types';
import { url } from './config';
import type {
    ChatUserProfile,
    ConversationReadStatusResult,
    CreateConversationBodyType,
    FindUserResult,
    SaveProfilePictureBodyType,
    UpdateMembersBodyType
} from './types';

const handleError = (error: unknown) => {
    console.error(`[messaging] api error:`, error);
    throw error;
};

const messagingApi = {
    async getConversations() {
        try {
            const res = await request.get(url);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async getMessages(conversationId: number, before?: number) {
        const params: any = { limit: 50 };
        if (before) params.before = before;
        try {
            const res = await request.get(`${url}/${conversationId}/messages`, { params });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async createConversation(body: CreateConversationBodyType) {
        try {
            const res = await request.post(url, body);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async sendMessage(conversationId: number, message: string, action?: string, replyToId?: number) {
        try {
            const res = await request.post(`${url}/${conversationId}/messages`, {
                message,
                action,
                replyToId
            });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async deleteMessage(messageId: number) {
        try {
            const res = await request.delete(`${url}/messages/${messageId}`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async editMessage(messageId: number, message: string) {
        try {
            const res = await request.put(`${url}/messages/${messageId}`, { message });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async markAsRead(conversationId: number) {
        try {
            const res = await request.put(`${url}/${conversationId}/read`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async getReadBy(conversationId: number) {
        try {
            const res = await request.get<{ readBy: ConversationReadStatusResult[] }>(
                `${url}/${conversationId}/read-by`
            );
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async getUnreadCount() {
        try {
            const res = await request.get(`${url}-unread`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async getMembers(conversationId: number) {
        try {
            const res = await request.get(`${url}/${conversationId}/members`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async updateMembers(conversationId: number, body: UpdateMembersBodyType) {
        try {
            const res = await request.put(`${url}/${conversationId}/members`, body);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async leaveConversation(conversationId: number) {
        try {
            const res = await request.delete(`${url}/${conversationId}`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async searchUsers(search?: string) {
        const params: any = {};
        if (search) params.search = search;
        try {
            const res = await request.get<FindUserResult[]>(`${url}-search-users`, { params });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async getOnlineUserIds() {
        try {
            const res = await request.get<number[]>(`${url}-online`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async getUserProfile(userId: number) {
        try {
            const res = await request.get<ChatUserProfile>(`${url}-users/${userId}`);
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** The large copy of a user's photo; null when only the thumbnail exists. */
    async getUserPhoto(userId: number): Promise<string | null> {
        try {
            const res = await request.get<{ photo: string }>(`${url}-users/${userId}/photo`);
            return res.data?.photo || null;
        } catch (error: any) {
            if (error?.response?.status === 404) return null;
            return handleError(error);
        }
    },

    /** The direct chat with this user — the existing one if there is one. Returns its id. */
    async openDirectChat(userId: number): Promise<number> {
        const result = await this.createConversation({ type: 'direct', memberIds: [userId] });
        return Number(result._id);
    },

    async saveProfilePicture(
        profilePicture: SaveProfilePictureBodyType['profilePicture'],
        profilePictureLarge?: SaveProfilePictureBodyType['profilePictureLarge']
    ) {
        try {
            const res = await request.put(`${url}-profile-picture`, { profilePicture, profilePictureLarge });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** One file per message; `onProgress` gets 0–100 while uploading. */
    async sendAttachment(
        conversationId: number,
        file: File,
        opts: { caption?: string; replyToId?: number; onProgress?: (percent: number) => void } = {}
    ) {
        const form = new FormData();
        form.append('file', file, file.name);
        if (opts.caption) form.append('caption', opts.caption);
        if (opts.replyToId) form.append('replyToId', String(opts.replyToId));
        try {
            const res = await request.post(`${url}/${conversationId}/attachments`, form, {
                onUploadProgress: (e) => {
                    if (e.total) opts.onProgress?.(Math.round((e.loaded / e.total) * 100));
                }
            });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** One of the built-in GIFs (gifCatalog.ts). */
    async sendGif(conversationId: number, gifId: string, replyToId?: number) {
        try {
            const res = await request.post(`${url}/${conversationId}/gifs`, { gifId, replyToId });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    /** Address of a chat file — the server checks membership, then redirects to a 5-minute link. */
    attachmentUrl(messageId: number, download = false) {
        return `${API_ENDPOINT}${url}-attachments/${messageId}${download ? '?download=1' : ''}`;
    },

    async toggleReaction(messageId: number, emoji: string) {
        try {
            const res = await request.post(`${url}/messages/${messageId}/reactions`, { emoji });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    },

    async searchMessages(conversationId: number, q: string) {
        try {
            const res = await request.get<MessageSearchHit[]>(`${url}/${conversationId}/search`, {
                params: { q }
            });
            return res.data;
        } catch (error) {
            return handleError(error);
        }
    }
} as const;

export default messagingApi;
